import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import './BackgroundDoodles.css';

// Import university logos
import usfLogo from '../../logos/usf.png';
import ucfLogo from '../../logos/ucf.png';
import ufLogo from '../../logos/uf.png';
import nyuLogo from '../../logos/nyu.png';
import uwLogo from '../../logos/uw.png';
import uclaLogo from '../../logos/ucla.png';
import buLogo from '../../logos/bu.png';
import calLogo from '../../logos/cal.png';

type Doodle = {
    key: string;
    fileName: string;
    src: string;
    spinSec: number;
    spinReverse: boolean;
};

// CRA/CRACO: load SVGs from the local `svgs/` folder.
// With CRA, `require()`/`require.context()` of an SVG typically yields a URL string.
const svgContext = (require as any).context('./svgs', false, /\.svg$/);

const getAllSvgAssets = (): Array<{ fileName: string; src: string }> => {
    const keys = svgContext.keys() as string[];
    const out: Array<{ fileName: string; src: string }> = [];
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const fileName = key.replace(/^\.\//, '');
        const mod = svgContext(key);
        const src =
            typeof mod === 'string'
                ? mod
                : (mod?.default as string | undefined) ?? (mod as string | undefined);

        if (typeof src === 'string' && src.length > 0) out.push({ fileName, src });
    }
    return out;
};

// Deterministic RNG so doodles don't reshuffle on every render.
const mulberry32 = (seed: number) => {
    return () => {
        let t = (seed += 0x6D2B79F5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
};

const pickN = <T,>(arr: T[], count: number, rand: () => number): T[] => {
    if (arr.length === 0) return [];
    if (arr.length >= count) {
        // Sample without replacement
        const copy = arr.slice();
        for (let i = copy.length - 1; i > 0; i--) {
            const j = Math.floor(rand() * (i + 1));
            const tmp = copy[i];
            copy[i] = copy[j];
            copy[j] = tmp;
        }
        return copy.slice(0, count);
    }
    // Not enough svgs: repeat to make `count`
    const out: T[] = [];
    while (out.length < count) {
        out.push(arr[Math.floor(rand() * arr.length)]);
    }
    return out;
};

interface BackgroundDoodlesProps {
    activeTabOffset?: number;
    activeTabWidth?: number;
}

const BackgroundDoodles: React.FC<BackgroundDoodlesProps> = ({ activeTabOffset = 0, activeTabWidth = 0 }) => {

    const [isReady, setIsReady] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const wrapperRefs = useRef<Array<HTMLDivElement | null>>([]);
    const simRef = useRef<{
        width: number;
        height: number;
        size: number;
        x: number[];
        y: number[];
        vx: number[];
        vy: number[];
        r: number[];
        lastT: number;
        raf: number | null;
    } | null>(null);

    const doodles = useMemo(() => {
        const baseSvgs = getAllSvgAssets();
        const uniLogos = [
            { fileName: 'usf.png', src: usfLogo },
            { fileName: 'ucf.png', src: ucfLogo },
            { fileName: 'uf.png', src: ufLogo },
            { fileName: 'nyu.png', src: nyuLogo },
            { fileName: 'uw.png', src: uwLogo },
            { fileName: 'ucla.png', src: uclaLogo },
            { fileName: 'bu.png', src: buLogo },
            { fileName: 'cal.png', src: calLogo },
        ];
        const svgs = [...baseSvgs, ...uniLogos];

        // seed once per mount
        const seed = Math.floor(Math.random() * 1_000_000_000);
        const rand = mulberry32(seed);

        const FIXED_COUNTS: Record<string, number> = {
            // 15 doodles (3 each) + 8 unis = 23 total
            'book.svg': 3,
            'happy.svg': 3,
            'heart.svg': 3,
            'house.svg': 3,
            'pencil.svg': 3,

            // 1 of each university
            'usf.png': 1,
            'ucf.png': 1,
            'uf.png': 1,
            'nyu.png': 1,
            'uw.png': 1,
            'ucla.png': 1,
            'bu.png': 1,
            'cal.png': 1,
        };

        const desiredTotal = Object.values(FIXED_COUNTS).reduce((sum, n) => sum + n, 0);

        const svgByLowerName = new Map<string, { fileName: string; src: string }>(
            svgs.map((s) => [s.fileName.toLowerCase(), s])
        );

        const chosen: Array<{ fileName: string; src: string }> = [];

        for (const [fileName, count] of Object.entries(FIXED_COUNTS)) {
            const asset =
                svgByLowerName.get(fileName) ??
                svgs.find((s) => s.fileName.toLowerCase().includes(fileName.replace(/\.svg$/i, '')));

            if (!asset) continue;
            for (let i = 0; i < count; i++) chosen.push(asset);
        }

        // If any expected SVGs are missing, fill remaining slots randomly.
        if (chosen.length < desiredTotal) {
            const fill = pickN(svgs, desiredTotal - chosen.length, rand);
            chosen.push(...fill);
        }

        const result: Doodle[] = [];

        for (let i = 0; i < chosen.length; i++) {
            const { fileName, src } = chosen[i];

            const spinSec = 18 + rand() * 18; // 18–36
            const spinReverse = rand() < 0.5;

            result.push({
                key: `${i}-${fileName}`,
                fileName,
                src,
                spinSec,
                spinReverse,
            });
        }

        return result;
    }, []);



    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const readSizePx = () => {
            const styles = getComputedStyle(container);
            const raw = styles.getPropertyValue('--doodle-size').trim();
            const parsed = Number.parseFloat(raw);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : 80;
        };

        const initSim = () => {
            const rect = container.getBoundingClientRect();
            const width = rect.width;
            const height = rect.height;
            const size = readSizePx();
            const radius = size / 2;

            const seed = Math.floor(Math.random() * 1_000_000_000);
            const rand = mulberry32(seed);

            const count = doodles.length;
            const x = new Array<number>(count);
            const y = new Array<number>(count);
            const vx = new Array<number>(count);
            const vy = new Array<number>(count);
            const r = new Array<number>(count).fill(radius);

            // place without overlap (simple rejection sampling)
            const maxAttempts = 2000;
            for (let i = 0; i < count; i++) {
                let placed = false;
                for (let attempt = 0; attempt < maxAttempts; attempt++) {
                    const px = radius + rand() * Math.max(1, width - size);
                    const py = radius + rand() * Math.max(1, height - size);

                    let ok = true;
                    for (let j = 0; j < i; j++) {
                        const dx = px - x[j];
                        const dy = py - y[j];
                        const min = r[i] + r[j] + 4; // small gap
                        if (dx * dx + dy * dy < min * min) {
                            ok = false;
                            break;
                        }
                    }

                    if (ok) {
                        x[i] = px;
                        y[i] = py;
                        placed = true;
                        break;
                    }
                }

                // If we failed to place (very small container), just stack them; collisions will separate.
                if (!placed) {
                    x[i] = radius + (i % 5) * (size + 4);
                    y[i] = radius + Math.floor(i / 5) * (size + 4);
                }

                // velocity (px/s) - slower float
                const speed = 6 + rand() * 10; // 6–16
                const angle = rand() * Math.PI * 2;
                vx[i] = Math.cos(angle) * speed;
                vy[i] = Math.sin(angle) * speed;
            }

            simRef.current = {
                width,
                height,
                size,
                x,
                y,
                vx,
                vy,
                r,
                lastT: performance.now(),
                raf: null,
            };

            // Apply initial positions immediately to avoid flash at (0,0)
            for (let i = 0; i < count; i++) {
                const el = wrapperRefs.current[i];
                if (el) {
                    el.style.transform = `translate(${x[i] - size / 2}px, ${y[i] - size / 2}px)`;
                }
            }
        };

        const step = (t: number) => {
            const sim = simRef.current;
            if (!sim) return;

            const dt = Math.min(0.05, Math.max(0.0, (t - sim.lastT) / 1000)); // clamp 0–50ms
            sim.lastT = t;

            const n = sim.x.length;
            const w = sim.width;
            const h = sim.height;
            const size = sim.size;
            // Allow icons to go out-of-bounds (past edges) before bouncing back.
            // These are bounds for the *center* of each icon.
            const margin = size; // allows up to ~1 icon size out of bounds
            const minX = -margin;
            const maxX = w + margin;
            const minY = -margin;
            const maxY = h + margin;

            // integrate + wall bounce
            for (let i = 0; i < n; i++) {
                sim.x[i] += sim.vx[i] * dt;
                sim.y[i] += sim.vy[i] * dt;

                if (sim.x[i] < minX) {
                    sim.x[i] = minX;
                    sim.vx[i] *= -1;
                } else if (sim.x[i] > maxX) {
                    sim.x[i] = maxX;
                    sim.vx[i] *= -1;
                }

                if (sim.y[i] < minY) {
                    sim.y[i] = minY;
                    sim.vy[i] *= -1;
                } else if (sim.y[i] > maxY) {
                    sim.y[i] = maxY;
                    sim.vy[i] *= -1;
                }
            }

            // pairwise collisions (equal mass elastic, with overlap correction)
            for (let i = 0; i < n; i++) {
                for (let j = i + 1; j < n; j++) {
                    const dx = sim.x[j] - sim.x[i];
                    const dy = sim.y[j] - sim.y[i];
                    const dist2 = dx * dx + dy * dy;
                    const minDist = sim.r[i] + sim.r[j];
                    if (dist2 === 0 || dist2 >= minDist * minDist) continue;

                    const dist = Math.sqrt(dist2);
                    const nx = dx / dist;
                    const ny = dy / dist;

                    // push apart
                    const overlap = minDist - dist + 0.5;
                    sim.x[i] -= nx * (overlap / 2);
                    sim.y[i] -= ny * (overlap / 2);
                    sim.x[j] += nx * (overlap / 2);
                    sim.y[j] += ny * (overlap / 2);

                    // relative velocity along normal
                    const rvx = sim.vx[j] - sim.vx[i];
                    const rvy = sim.vy[j] - sim.vy[i];
                    const velAlongNormal = rvx * nx + rvy * ny;
                    if (velAlongNormal > 0) continue;

                    // perfectly elastic for equal mass: impulse = -2 * v_n / (m1+m2) with m=1 => -v_n
                    const impulse = -velAlongNormal;
                    sim.vx[i] -= impulse * nx;
                    sim.vy[i] -= impulse * ny;
                    sim.vx[j] += impulse * nx;
                    sim.vy[j] += impulse * ny;
                }
            }

            // write transforms
            for (let i = 0; i < n; i++) {
                const el = wrapperRefs.current[i];
                if (!el) continue;
                el.style.transform = `translate(${sim.x[i] - size / 2}px, ${sim.y[i] - size / 2}px)`;
            }

            sim.raf = requestAnimationFrame(step);
        };

        const ro = new ResizeObserver(() => {
            // reset simulation when container size changes (keeps non-overlap guarantees)
            initSim();
        });

        ro.observe(container);
        initSim();
        setIsReady(true);
        simRef.current!.raf = requestAnimationFrame(step);

        return () => {
            ro.disconnect();
            const sim = simRef.current;
            if (sim?.raf) cancelAnimationFrame(sim.raf);
            simRef.current = null;
        };
    }, [doodles.length]);

    return (
        <div
            ref={containerRef}
            className={`background-doodles ${isReady ? 'is-ready' : ''}`}
            style={{
                '--tab-offset': `${activeTabOffset}px`,
                '--tab-width': `${activeTabWidth}px`
            } as React.CSSProperties}
        >

            {doodles.map((d, idx) => (
                <div
                    key={d.key}
                    className="doodle-random"
                    aria-hidden="true"
                    ref={(el) => {
                        wrapperRefs.current[idx] = el;
                    }}
                >
                    {(() => {
                        const isLogo = d.fileName.toLowerCase().endsWith('.png');
                        const style: React.CSSProperties = {
                            animation: `doodleSpin ${d.spinSec}s linear infinite`,
                            animationDirection: d.spinReverse ? 'reverse' : 'normal',
                        };

                        if (isLogo) {
                            style.backgroundImage = `url(${d.src})`;
                        } else {
                            style.WebkitMaskImage = `url(${d.src})`;
                            style.maskImage = `url(${d.src})`;
                            if (d.fileName.toLowerCase() === 'apple.svg') {
                                (style as any)['--doodle-scale'] = 0.7;
                            } else if (d.fileName.toLowerCase() === 'apartment.svg') {
                                (style as any)['--doodle-scale'] = 1.35;
                            }
                        }

                        return (
                            <div
                                className={`doodle-icon${isLogo ? ' is-logo' : ''}`}
                                aria-hidden="true"
                                style={style}
                            />
                        );
                    })()}
                </div>
            ))}

            {/* SVG Filter definitions for stamped look */}
            <svg style={{ position: 'absolute', width: 0, height: 0 }} aria-hidden="true">
                <defs>
                    <filter id="green-stamp" colorInterpolationFilters="sRGB">
                        {/* 1. Extract Luminance to Alpha (White=Opaque, Black=Transparent) */}
                        <feColorMatrix type="luminanceToAlpha" in="SourceGraphic" result="luma" />

                        {/* 2. Invert Luma (White=Transparent, Black=Opaque) so dark ink = visible */}
                        <feComponentTransfer in="luma" result="inv-luma">
                            <feFuncA type="linear" slope="-1" intercept="1" />
                        </feComponentTransfer>

                        {/* 3. Get Original Alpha to ensure we respect existing transparency */}
                        <feColorMatrix type="matrix" in="SourceGraphic"
                            values="0 0 0 0 0
                                    0 0 0 0 0
                                    0 0 0 0 0
                                    0 0 0 1 0" result="src-alpha" />

                        {/* 4. Combine Inv-Luma with Src-Alpha (Intersection) */}
                        <feComposite in="inv-luma" in2="src-alpha" operator="in" result="masked" />

                        {/* 5. Create Solid Green Flood (#2E7D32) */}
                        <feFlood floodColor="#2E7D32" result="green-flood" />

                        {/* 6. Composite Green Flood into the Masked Shape */}
                        <feComposite in="green-flood" in2="masked" operator="in" />
                    </filter>

                    <filter id="green-stamp-dark" colorInterpolationFilters="sRGB">
                        <feColorMatrix type="luminanceToAlpha" in="SourceGraphic" result="luma" />
                        <feComponentTransfer in="luma" result="inv-luma">
                            <feFuncA type="linear" slope="-1" intercept="1" />
                        </feComponentTransfer>
                        <feColorMatrix type="matrix" in="SourceGraphic"
                            values="0 0 0 0 0
                                    0 0 0 0 0
                                    0 0 0 0 0
                                    0 0 0 1 0" result="src-alpha" />
                        <feComposite in="inv-luma" in2="src-alpha" operator="in" result="masked" />
                        <feFlood floodColor="#4CAF50" result="green-flood" />
                        <feComposite in="green-flood" in2="masked" operator="in" />
                    </filter>
                </defs>
            </svg>


        </div>
    );
};

export default BackgroundDoodles;
